import * as React from 'react';
import { off, on } from 'react-use/lib/misc/util';
import { betweenWidth, fromWidth, toWidth } from '@doltech/core/lib/responsive/media-query';

const isServerSide = typeof window === 'undefined';

interface ShouldRenderOnScreenProps {
  fromScreen?: number;
  toScreen?: number;
  defaultValue?: boolean;
}

const useShouldRender = ({ fromScreen, toScreen, defaultValue }: ShouldRenderOnScreenProps) => {
  const [shouldRender, setShouldRender] = React.useState(false);

  React.useEffect(() => {
    let query = null;
    if (fromScreen !== undefined && toScreen !== undefined) {
      query = betweenWidth(fromScreen, toScreen);
    } else if (fromScreen !== undefined) {
      query = fromWidth(fromScreen);
    } else {
      query = toWidth(toScreen);
    }

    const onChange = () => {
      setShouldRender(window.matchMedia(query).matches);
    };

    on(window, 'resize', onChange);
    onChange();

    return () => {
      off(window, 'resize', onChange);
    };
  }, [fromScreen, toScreen]);

  return shouldRender;
};

/**
 * Component using this HOC should be rendered on client-side only ( watch out Hydration miss-match if using on SSR as using media query for rendering )
 * @param fromScreen
 * @param toScreen
 */
export const withShouldRenderOnScreen =
  (opt: ShouldRenderOnScreenProps) => (Component: any) => (props: any) => {
    const shouldRender = useShouldRender(opt);

    if (shouldRender) {
      // This component should be wrapped with dynamic import function
      return <Component {...props} />;
    }
    return <DefaultRenderer {...props} />;
  };

const DefaultRenderer = () => null;

export const shouldRenderOnScreen = (options: ShouldRenderOnScreenProps) => (Component) => {
  if (isServerSide) {
    return DefaultRenderer;
  }

  // Client side check
  const { fromScreen, toScreen } = options;
  let query = null;
  if (fromScreen !== undefined && toScreen !== undefined) {
    query = betweenWidth(fromScreen, toScreen);
  } else if (fromScreen !== undefined) {
    query = fromWidth(fromScreen);
  } else {
    query = toWidth(toScreen);
  }
  const shouldRender = window.matchMedia(query).matches;

  if (shouldRender) {
    return withShouldRenderOnScreen({ fromScreen, toScreen, defaultValue: true })(Component);
  }

  return withShouldRenderOnScreen({ fromScreen, toScreen, defaultValue: false })(Component);
};
